import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, Check, CreditCard, CheckCircle, AlertCircle, X } from 'lucide-react';
import axios from 'axios';
import Base from '../Base';

const CheckoutForm = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [formData, setFormData] = useState({
    pageTitle: 'Checkout',
    checkoutStyle: 'single-page', // single-page or multi-step
    showOrderSummary: true,
    requireAccountCreation: false,
    enableGuestCheckout: true,
    showCouponField: true,
    showGiftMessage: false,
    showNewsletterOptIn: true,
    newsletterText: 'Subscribe to our newsletter for updates and offers',
    termsAndConditions: {
      enabled: true,
      text: 'I agree to the terms and conditions',
      link: '/terms'
    },
    shippingMethods: ['Standard', 'Express', 'Overnight'],
    paymentMethods: ['Credit Card', 'PayPal', 'Cash on Delivery']
  });

  useEffect(() => {
    loadPageData();
  }, [templateId]);

  const loadPageData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`/api/template/${templateId}/page/checkout`, {
        headers: { 'Authorization': token ? `Bearer ${token}` : '' }
      });
      if (response.data.content) {
        setFormData(response.data.content);
      }
    } catch (error) {
      console.error('Error loading checkout page data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    navigate(`/template-builder/${templateId}`);
  };

  const handleSave = async (isDraft = true) => {
    try {
      setSaving(true);
      const token = localStorage.getItem('access_token');
      await axios.post(`/api/template/${templateId}/page/checkout/save`, {
        status: isDraft ? 'draft' : 'complete',
        content: formData
      }, {
        headers: { 'Authorization': token ? `Bearer ${token}` : '' }
      });
      setLastSaved(new Date());
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error saving:', error);
      setErrorMessage(error.response?.data?.message || 'Error saving changes. Please try again.');
      setShowErrorModal(true);
    } finally {
      setSaving(false);
    }
  };

  // Success Modal Component - Vasstr colors
  const SuccessModal = () => {
    if (!showSuccessModal) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/50" onClick={handleSuccessModalClose}></div>
        <div className="relative bg-white rounded-2xl shadow-xl max-w-sm w-full overflow-hidden border border-gray-200">
          <div className="p-6 text-center">
            <div className="w-16 h-16 bg-[#D45614]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-[#D45614]" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Saved Successfully!</h3>
            <p className="text-gray-600 text-sm mb-6">Your checkout page has been saved.</p>
            <button onClick={handleSuccessModalClose} className="w-full bg-[#D45614] hover:bg-[#b8480f] text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2">
              <Check className="w-4 h-4" />
              Continue to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Error Modal Component - Simple design
  const ErrorModal = () => {
    if (!showErrorModal) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/50" onClick={() => setShowErrorModal(false)}></div>
        <div className="relative bg-white rounded-2xl shadow-xl max-w-sm w-full overflow-hidden border border-gray-200">
          <div className="p-6 text-center">
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h3>
            <p className="text-gray-600 text-sm mb-6">{errorMessage}</p>
            <button onClick={() => setShowErrorModal(false)} className="w-full bg-gray-800 hover:bg-gray-900 text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2">
              <X className="w-4 h-4" />
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Base Content={
        <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading checkout page...</p>
        </div>
      </div>
    } />
    );
  }

  const FormContent = (
    <div className="min-h-screen bg-white">
      <div className="sticky top-0 z-50 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate(`/template-builder/${templateId}`)} className="p-2 hover:bg-gray-100 rounded-lg">
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-lg font-bold text-gray-900">Checkout Page</h1>
                {lastSaved && <p className="text-xs text-gray-500">Last saved {lastSaved.toLocaleTimeString()}</p>}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">
                <Eye className="w-4 h-4" />
                <span className="hidden sm:inline">Preview</span>
              </button>
              <button onClick={() => handleSave(false)} disabled={saving} className="flex items-center gap-2 px-6 py-2 bg-[#D45614] text-white rounded-lg hover:bg-[#D45614]-dark disabled:opacity-50">
                {saving ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div><span>Saving...</span></> : <><Check className="w-4 h-4" /><span>Save & Complete</span></>}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm border p-6 space-y-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#D45614]-light rounded-lg flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Checkout Configuration</h2>
              <p className="text-sm text-gray-600">Configure your checkout process</p>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Page Title</label>
              <input type="text" value={formData.pageTitle} onChange={(e) => setFormData(prev => ({ ...prev, pageTitle: e.target.value }))} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Checkout Style</label>
              <select value={formData.checkoutStyle} onChange={(e) => setFormData(prev => ({ ...prev, checkoutStyle: e.target.value }))} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary">
                <option value="single-page">Single Page Checkout</option>
                <option value="multi-step">Multi-Step Checkout</option>
              </select>
            </div>

            <div className="space-y-3">
              <h3 className="font-semibold text-gray-900">General Settings</h3>
              {[
                { id: 'showOrderSummary', label: 'Show Order Summary Sidebar' },
                { id: 'enableGuestCheckout', label: 'Enable Guest Checkout' },
                { id: 'requireAccountCreation', label: 'Require Account Creation' },
                { id: 'showCouponField', label: 'Show Coupon/Discount Field' },
                { id: 'showGiftMessage', label: 'Enable Gift Message Option' },
                { id: 'showNewsletterOptIn', label: 'Show Newsletter Opt-in' }
              ].map(feature => (
                <div key={feature.id} className="flex items-center gap-3 p-3 bg-white rounded-lg">
                  <input type="checkbox" id={feature.id} checked={formData[feature.id]} onChange={(e) => setFormData(prev => ({ ...prev, [feature.id]: e.target.checked }))} className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary" />
                  <label htmlFor={feature.id} className="text-sm font-medium text-gray-700">{feature.label}</label>
                </div>
              ))}
            </div>

            {formData.showNewsletterOptIn && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Newsletter Opt-in Text</label>
                <input type="text" value={formData.newsletterText} onChange={(e) => setFormData(prev => ({ ...prev, newsletterText: e.target.value }))} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
              </div>
            )}

            <div className="border-t pt-4">
              <h3 className="font-semibold text-gray-900 mb-3">Terms & Conditions</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-white rounded-lg">
                  <input type="checkbox" id="termsEnabled" checked={formData.termsAndConditions.enabled} onChange={(e) => setFormData(prev => ({ ...prev, termsAndConditions: { ...prev.termsAndConditions, enabled: e.target.checked } }))} className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary" />
                  <label htmlFor="termsEnabled" className="text-sm font-medium text-gray-700">Require Terms & Conditions Acceptance</label>
                </div>

                {formData.termsAndConditions.enabled && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Checkbox Text</label>
                      <input type="text" value={formData.termsAndConditions.text} onChange={(e) => setFormData(prev => ({ ...prev, termsAndConditions: { ...prev.termsAndConditions, text: e.target.value } }))} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Terms Page Link</label>
                      <input type="text" value={formData.termsAndConditions.link} onChange={(e) => setFormData(prev => ({ ...prev, termsAndConditions: { ...prev.termsAndConditions, link: e.target.value } }))} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> Shipping methods and payment gateways are configured separately in your store settings.
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Modals */}
      <SuccessModal />
      <ErrorModal />
    </div>
  );

  return <Base Content={FormContent} />;
};

export default CheckoutForm;
